/* eslint-disable no-loop-func */

import { useDebounce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';

import { Button, Input, Label, Paragraph, Popover, Skeleton, Subheading } from '@treams/treams-ui';
import { CloseIcon, PencilIcon, SearchIcon } from '@treams/treams-icons';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';

import Breadcrumbs from './Breadcrumbs';
import Group from './Group';
import Row from './Row';

import trim from './trim';
import { useField } from 'formik';

const Clip = styled(Box)`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	overflow-y: auto;
`;

const Grid = styled(Flex)`
	position: relative;
	flex-direction: column;
`;

const AddButton = styled(Box)<{ enabled?: boolean }>`
	background: ${(props) => props.theme.colors.lightBlue};
	width: 32px;
	height: 32px;
	padding: 4px;
	border-radius: 16px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
	}

	${(props) =>
		props.enabled !== false &&
		`
		cursor: pointer;
		
		&:hover {
			background: ${props.theme.colors.lightBlueDark};

			&:active {
				background: ${props.theme.colors.slateBlue};

				svg {
					path {
						fill: ${props.theme.colors.white};
					}
				}
			}
		}`}
`;

function simplify(groups: graphql.Group[]): graphql.Group[] {
	const set = new Set(groups.map(({ id }) => id));
	return groups.filter((group) => !group.parent || !set.has(group.parent?.id));
}

function Subtree(props: {
	depth: number;
	groups: graphql.Group[];
	parent?: string;
	root?: boolean;
	selected: (group: graphql.Group) => { selected: boolean; recursive: boolean; specific: boolean };
	onSelect?: (group: graphql.Group, recursive: boolean) => void;
	onDeselect?: (group: graphql.Group) => void;
	single?: boolean;
	canSelect: (group: graphql.Group) => boolean;
}) {
	const { t } = useTranslation();

	let firstTeam = true;

	return (
		<Box ml={props.depth > 0 ? 24 : 0}>
			{_.sortBy(props.groups, (group) => group.type !== graphql.GroupType.Department)
				.filter(
					(group) =>
						group.parent?.id === props.parent ||
						(props.root && group.id !== props.parent && !group.parent),
				)
				.map((group) => {
					const { selected, recursive, specific } = props.selected(group);

					return (
						<Box key={group.id} ml={group.type === graphql.GroupType.Team ? -32 : 0}>
							{firstTeam && group.type === graphql.GroupType.Team && !(firstTeam = false) && (
								<Label px={2} mt={3} ml={32}>
									{t('groups.selector.heading.teams')}
								</Label>
							)}
							<Row
								group={group}
								hasChildren={props.groups.some((child) => child.parent?.id === group.id)}
								recursive={recursive}
								disabled={selected && !specific}
								single={props.single ?? false}
								selected={selected}
								canSelect={props.canSelect(group)}
								onSelect={
									props.onSelect ? (recursive) => props.onSelect!(group, recursive) : undefined
								}
								onDeselect={
									specific && props.onDeselect ? () => props.onDeselect!(group) : undefined
								}
							>
								<Subtree
									depth={props.depth + 1}
									groups={props.groups}
									parent={group.id}
									onSelect={props.onSelect}
									onDeselect={props.onDeselect}
									single={props.single}
									canSelect={props.canSelect}
									selected={props.selected}
								/>
							</Row>
						</Box>
					);
				})}
		</Box>
	);
}

function StandaloneRow(props: {
	depth: number;
	group: graphql.Group;
	parent?: string;
	root?: boolean;
	selected: (group: graphql.Group) => { selected: boolean; recursive: boolean; specific: boolean };
	onSelect?: (group: graphql.Group, recursive: boolean) => void;
	onDeselect?: (group: graphql.Group) => void;
	single?: boolean;
	canSelect: (group: graphql.Group) => boolean;
}) {
	const { selected, recursive, specific } = props.selected(props.group);

	return (
		<Row
			group={props.group}
			hasChildren={!!props.parent}
			recursive={recursive}
			disabled={selected && !specific}
			single={props.single ?? false}
			selected={selected}
			canSelect={props.canSelect(props.group)}
			onSelect={props.onSelect ? (recursive) => props.onSelect!(props.group, recursive) : undefined}
			onDeselect={specific && props.onDeselect ? () => props.onDeselect!(props.group) : undefined}
		/>
	);
}

/// This is a temporary fix to prevent overwhelming the server.
function useBatchedGroups(props: { types?: graphql.GroupType[] }) {
	const [groups, setGroups] = useState<graphql.Group[]>([]);
	const [finished, setFinished] = useState(false);

	const client = useApolloClient();

	useEffect(() => {
		(async () => {
			let endCursor: string | undefined;

			do {
				const batch = await client.query<graphql.GroupsQuery>({
					query: graphql.GroupsDocument,
					variables: {
						first: 100,
						after: endCursor,
						filter: {
							isArchived: false,
							types: props.types,
						},
					},
				});

				setGroups((groups) =>
					groups.concat(batch.data.groups.edges.map(({ node }) => node as graphql.Group)),
				);

				endCursor = batch.data.groups.pageInfo.endCursor ?? undefined;
			} while (endCursor);

			setFinished(true);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return finished ? groups : undefined;
}

export function useInitialGroups(options: {
	types?: graphql.GroupType[];
	canSelect: (group: graphql.Group) => boolean;
}):
	| {
			groups: graphql.Group[];
			initialGroups: { group: graphql.Group; recursive: boolean }[];
	  }
	| undefined {
	const groups = useBatchedGroups({ types: options.types });
	const rolesQuery = graphql.useViewerRolesQuery({ variables: {} });

	if (!groups || !rolesQuery.data) {
		return undefined;
	}

	const roles = rolesQuery.data.viewer.roles as graphql.UserRole[];

	const result = roles.flatMap((role) => {
		if (role.targetV2?.__typename === 'User') {
			// Not applicable.
		} else if (role.targetV2?.__typename === 'Group') {
			const group = groups.find((group) => group.id === role.targetV2!.id);

			if (group && options.canSelect(group)) {
				return [
					{
						group,
						recursive: false,
					},
				];
			}
		} else {
			const group = groups.find(
				(group) => group.type === graphql.GroupType.Department && !group.parent,
			);

			if (group && options.canSelect(group)) {
				return [
					{
						group,
						recursive: false,
					},
				];
			}
		}

		return [];
	});

	return {
		groups,
		initialGroups: _.uniqBy(result, ({ group }) => group.id),
	};
}

export function InvisibleGroupSelector(props: {
	types?: graphql.GroupType[];
	canSelect: (group: graphql.Group) => boolean;
	onChange: (groups: graphql.Group[]) => void;
}) {
	const result = useInitialGroups({ types: props.types, canSelect: props.canSelect });

	useEffect(() => {
		if (result) {
			props.onChange(result.initialGroups.map(({ group }) => group));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!result]);

	return null;
}

export default function GroupSelector(props: {
	single?: boolean;
	disabled?: boolean;
	required?: boolean;
	remember?: boolean | string;
	types?: graphql.GroupType[];
	initialGroups?: { group: graphql.Group; recursive: boolean }[];
	canSelect: (group: graphql.Group) => boolean;
	onChange: (groups: graphql.Group[]) => void;
}) {
	const result = useInitialGroups({ types: props.types, canSelect: props.canSelect });

	if (!result) {
		return null;
	}

	return (
		<GroupSelectorWithData
			{...props}
			groups={result.groups}
			initialGroups={props.initialGroups ?? result.initialGroups}
		/>
	);
}

function groupsFromStorage(key: string, params: URLSearchParams, groups: graphql.Group[]) {
	const serialized = params.get('group_ids') ?? window.localStorage.getItem(key);
	const groupIDs = serialized?.split(';');

	if (!groupIDs || groupIDs.length === 0) {
		return undefined;
	}

	return groupIDs.flatMap((id) => {
		const recursive = id.endsWith('+');
		const group = groups.find((group) => group.id === id.split('+')[0]);

		if (!group) {
			return [];
		}

		return [{ group, recursive }];
	});
}

function GroupSelectorWithData(props: {
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
	initialGroups?: { group: graphql.Group; recursive: boolean }[];
	groups: graphql.Group[];
	remember?: boolean | string;
	canSelect: (group: graphql.Group) => boolean;
	onChange: (groups: graphql.Group[]) => void;
}) {
	const { t } = useTranslation();

	const storageKey = typeof props.remember === 'string' ? props.remember : 'groupIDs';

	const groups = props.groups;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const tree = useMemo(() => trim(groups, props.canSelect), []);

	const root = useMemo(
		() => tree.find((group) => group.type === graphql.GroupType.Department && !group.parent)!.id,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const [start, setStart] = useState<string | undefined>(root);
	const [visible, setVisible] = useState(false);
	const history = useHistory();
	const params = new URLSearchParams(history.location.search);
	const [selection, setSelection] = useState<
		{
			group: graphql.Group;
			recursive: boolean;
		}[]
	>(
		() =>
			(props.remember ? groupsFromStorage(storageKey, params, groups) : undefined) ??
			props.initialGroups ??
			simplify(tree.filter(props.canSelect))
				.filter((group) => group.type !== graphql.GroupType.Team)
				.map((group) => ({
					group,
					recursive: true,
				})),
	);

	const [flatSelection, setFlatSelection] = useState<graphql.Group[]>([]);

	useEffect(() => {
		if (query === '') {
			return;
		}

		setLoading(true);
	}, [query]);

	useLayoutEffect(() => {
		if (props.remember) {
			const groupIDs = selection.map(({ group, recursive }) => group.id + (recursive ? '+' : ''));
			window.localStorage.setItem(storageKey, groupIDs.join(';'));
		}

		const flattened = selection.flatMap(({ group, recursive }) => {
			if (!recursive) {
				if (!props.canSelect(group)) {
					return [];
				}

				return [group];
			}

			const stack: graphql.Group[] = [group];
			const results: graphql.Group[] = [];
			const visited = new Set();

			while (stack.length > 0) {
				const parent = stack.pop()!;

				if (visited.has(parent.id)) {
					continue;
				}

				visited.add(parent.id);

				if (props.canSelect(parent)) {
					results.push(parent);
				}

				stack.push(...tree.filter((group) => group.parent?.id === parent.id));
			}

			return results;
		});

		setFlatSelection(flattened);
		props.onChange(
			_.uniqBy(
				_.sortBy(flattened, (group) => group.name),
				(group) => group.id,
			),
		);

		if (props.single && flattened.length > 0) {
			setVisible(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection.map(({ group: { id }, recursive }) => (recursive ? `${id}+` : id)).join(',')]);

	useDebounce(
		() => {
			if (query === '') {
				setLoading(false);

				return;
			}

			const match = groups.find((group) => group.name.toLowerCase().includes(query.toLowerCase()));

			if (match) {
				if (groups.some((group) => group.parent?.id === match.id)) {
					setStart(match.id);
				} else {
					setStart(match.parent!.id);
				}
			} else {
				setStart(undefined);
			}

			setLoading(false);
		},
		200,
		[query],
	);

	const breadcrumbs = useMemo(() => {
		const breadcrumbs: graphql.Group[] = [];
		const visited = new Set();

		let needle: string | undefined = start;

		while (needle !== undefined) {
			if (visited.has(needle)) {
				break;
			}

			visited.add(needle);

			const match = groups.find((group) => group.id === needle)!;
			breadcrumbs.push(match);
			needle = match.parent?.id;
		}

		breadcrumbs.reverse();

		return breadcrumbs;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [start]);

	const onAdd = () => {
		if (props.required && props.single) {
			setVisible(true);
			return;
		}

		if (props.single && selection.length > 0) {
			setSelection([]);
			return;
		}

		setVisible(true);
	};

	return (
		<>
			<Flex alignItems="center" flexWrap="wrap">
				{selection.map(({ group, recursive }) => (
					<Box mr={2} mb={2} key={group.id}>
						<Group
							query=""
							group={group}
							hasChildren={false}
							recursive={recursive}
							single={props.single ?? false}
							canSelect={props.canSelect(group)}
							onSelect={(recursive) => {
								if (props.single) {
									setSelection([{ group, recursive }]);
								} else {
									setSelection((selection) =>
										selection
											.filter((match) => match.group.id !== group.id)
											.concat([{ group, recursive }]),
									);
								}
							}}
							onDeselect={() => {
								setSelection((selection) =>
									selection.filter((match) => match.group.id !== group.id),
								);
							}}
						/>
					</Box>
				))}
			</Flex>
			{!props.disabled && (
				<Popover.Target
					center
					visible={visible}
					onDismiss={() => setVisible(false)}
					contents={
						<Grid width="800px" height="600px">
							<Flex p={3}>
								<Box width={400} flexGrow={0} flexShrink={0}>
									<Input
										autoFocus
										search
										loading={loading}
										value={query}
										onChange={(query) => {
											if (query === '') {
												setStart(root);
											}

											setQuery(query);
										}}
										icon={<SearchIcon />}
										placeholder={t('groups.selector.search.placeholder')}
									/>
								</Box>
								<Box flexGrow={1} />
								<Button
									type="primary"
									title={t('groups.selector.search.done')}
									flexGrow={0}
									flexShrink={0}
									ml={2}
									onClick={() => setVisible(false)}
								/>
							</Flex>

							<Box px={4} flexGrow={0} flexShrink={0} mb={2}>
								<Breadcrumbs
									components={breadcrumbs}
									onChange={(id) => {
										setQuery('');
										setStart(id);
									}}
								/>
							</Box>

							{!start ? (
								<Clip py={3} px={4}>
									<Subheading>{t('groups.selector.heading.notFound')}</Subheading>
									<Paragraph>{t('groups.selector.notFound')}</Paragraph>
								</Clip>
							) : (
								<Clip>
									{start === root &&
										tree.some((group) => group.type === graphql.GroupType.Team) && (
											<Label px={2} mt={2}>
												{t('groups.selector.heading.departments')}
											</Label>
										)}
									<Box ml={-32}>
										<StandaloneRow
											group={groups.find((group) => group.id === start)!}
											depth={0}
											parent={start}
											root={breadcrumbs.length === 1}
											selected={(group) => ({
												selected: flatSelection.some((match) => group.id === match.id),
												specific: selection.some((match) => match.group.id === group.id),
												recursive:
													selection.find((match) => match.group.id === group.id)?.recursive ??
													false,
											})}
											single={props.single}
											canSelect={props.canSelect}
											onSelect={(group, recursive) => {
												if (props.single) {
													setSelection([{ group, recursive }]);
												} else {
													setSelection((selection) =>
														selection
															.filter((match) => match.group.id !== group.id)
															.concat([{ group, recursive }]),
													);
												}
											}}
											onDeselect={(group) => {
												setSelection((selection) =>
													selection.filter((match) => match.group.id !== group.id),
												);
											}}
										/>
									</Box>
									<Subtree
										depth={0}
										groups={tree}
										parent={start}
										root={breadcrumbs.length === 1}
										selected={(group) => ({
											selected: flatSelection.some((match) => group.id === match.id),
											specific: selection.some((match) => match.group.id === group.id),
											recursive:
												selection.find((match) => match.group.id === group.id)?.recursive ?? false,
										})}
										single={props.single}
										canSelect={props.canSelect}
										onSelect={(group, recursive) => {
											if (props.single) {
												setSelection([{ group, recursive }]);
											} else {
												setSelection((selection) =>
													selection
														.filter((match) => match.group.id !== group.id)
														.concat([{ group, recursive }]),
												);
											}
										}}
										onDeselect={(group) => {
											setSelection((selection) =>
												selection.filter((match) => match.group.id !== group.id),
											);
										}}
									/>
								</Clip>
							)}
						</Grid>
					}
				>
					<Flex height={40} alignItems="center" mb={2}>
						{props.required && props.single ? (
							<AddButton onClick={onAdd}>
								<PencilIcon />
							</AddButton>
						) : props.single && selection.length > 0 ? (
							<AddButton onClick={onAdd}>
								<CloseIcon />
							</AddButton>
						) : (
							<Flex>
								<Button
									onClick={onAdd}
									type="transparent"
									title={t('groups.selector.change.button')}
									flexShrink={0}
									flexWrap="nowrap"
									small
								/>
							</Flex>
						)}
					</Flex>
				</Popover.Target>
			)}
		</>
	);
}

GroupSelector.Skeleton = function GroupSelectorSkeleton(props: {
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<>
			<Flex alignItems="center">
				<Skeleton.Repeat count={props.single ? 1 : 3}>
					<Box mr={2} mb={2}>
						<Group.Skeleton />
					</Box>
				</Skeleton.Repeat>
			</Flex>
			<Flex>
				{!props.disabled && (
					<Box mb={2}>
						{props.single && props.required ? (
							<AddButton enabled={false}>
								<PencilIcon />
							</AddButton>
						) : (
							<Button
								type="transparent"
								title={t('groups.selector.change.button')}
								flexShrink={0}
								flexWrap="nowrap"
								small
								disabled
							/>
						)}
					</Box>
				)}
			</Flex>
		</>
	);
};

GroupSelector.Formik = function GroupSelectorFormik(props: {
	name: string;
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
	types?: graphql.GroupType[];
	canSelect: (group: graphql.Group) => boolean;
}) {
	const [, { value }, { setValue }] = useField<string[]>(props.name);

	const groups = graphql.useGroupsQuery({
		variables: { first: 1000, filter: { types: props.types } },
	});

	if (!groups.data) {
		return <GroupSelector.Skeleton />;
	}

	const nodes = groups.data?.groups.edges.map(({ node }) => node as graphql.Group);

	return (
		<GroupSelectorWithData
			canSelect={props.canSelect}
			onChange={(groups) => setValue(groups.map(({ id }) => id))}
			disabled={props.disabled}
			initialGroups={nodes
				.filter(({ id }) => value.includes(id))
				.map((group) => ({ group, recursive: false }))}
			groups={nodes}
		/>
	);
};
